import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useMatch, useNavigate, useOutletContext, useParams } from "react-router-dom";

import AppShell from "../components/layout/AppShell";
import EmailDetailDrawer from "../components/history/EmailDetailDrawer";
import { InboxAuditPromoEmptyState } from "../components/audit/InboxAuditPromoEmptyState.jsx";
import Badge from "../components/ui/Badge";
import Card from "../components/ui/Card";
import { PageFirstLoadSpinner } from "../components/ui/PageFirstLoadSpinner.jsx";
import { useMailboxScope } from "../contexts/MailboxScopeContext";
import { historyService } from "../services/history";
import { brandProfileService } from "../services/brandProfile";
import { getScoreBadgeClass } from "../utils/scoreColors";

const PAGE_SIZE = 10;

const SORT_HUMAN = "is_from_human_confidence";
const SORT_PITCH = "is_cold_pitch_confidence";

function BoolConfidenceCell({ flag, confidence }) {
  const pct = Number(confidence ?? 0).toFixed(0);
  const icon = flag ? "check" : "close";
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span
        className={`material-symbols-outlined text-[18px] leading-none shrink-0 ${flag ? "text-theme-accent" : "text-theme-dim opacity-70"}`}
        aria-hidden
      >
        {icon}
      </span>
      <span>{pct}%</span>
    </span>
  );
}

const HISTORY_KIND_OPTIONS = [
  { value: "all", label: "All Emails" },
  { value: "pitches", label: "Incoming Pitches" },
  { value: "matches", label: "Matches" },
];

export function HistoryDetailOutlet() {
  const { inboundHash } = useParams();
  const navigate = useNavigate();
  const { historyBrandHash = "" } = useOutletContext() || {};
  if (!inboundHash) return null;
  return (
    <EmailDetailDrawer
      inboundHash={inboundHash}
      brandHash={historyBrandHash}
      onClose={() => navigate("/history/")}
    />
  );
}

const History = (props) => {
  const navigate = useNavigate();
  const detailMatch = useMatch({ path: "/history/:inboundHash/", end: true });
  const inboundHash = detailMatch?.params?.inboundHash ?? "";
  const [sortField, setSortField] = useState("received_at");
  const [sortDirection, setSortDirection] = useState("desc");
  /** When set, table lists only human yes or pitch yes rows and sorts by that confidence. */
  const [confidenceFilter, setConfidenceFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [brandFilter, setBrandFilter] = useState("");
  const [historyKind, setHistoryKind] = useState("all");
  const [prospectSearch, setProspectSearch] = useState("");
  const mailboxScope = useMailboxScope();

  const brandsQuery = useQuery({
    queryKey: ["brand-profiles-all"],
    queryFn: brandProfileService.listAll,
  });
  const brands = brandsQuery.data?.results ?? [];
  const showBrandSelect = brands.length > 1;

  useEffect(() => {
    if (showBrandSelect) return;
    setBrandFilter("");
  }, [showBrandSelect]);

  const historyQuery = useQuery({
    queryKey: ["history-list", mailboxScope?.mailboxHash, brandFilter, historyKind],
    queryFn: () =>
      historyService.getList(mailboxScope?.mailboxHash || "", brandFilter, historyKind),
    enabled: Boolean(mailboxScope?.mailboxHash) && !mailboxScope?.mailboxesLoading,
  });

  const scopeBooting = Boolean(mailboxScope?.mailboxesLoading);
  const mailboxReady = Boolean(mailboxScope?.mailboxHash) && !scopeBooting;
  const showHistoryBodySpinner = scopeBooting || (mailboxReady && historyQuery.isLoading);

  const rows = historyQuery.data?.results || [];
  const showAuditPromo = mailboxReady && !historyQuery.isLoading && rows.length === 0 && historyKind === "all";
  const showFilteredEmpty =
    mailboxReady && !historyQuery.isLoading && rows.length === 0 && historyKind !== "all";
  const showHistoryToolbar = mailboxReady;
  const showHistoryTable = Boolean(mailboxScope?.mailboxHash) && rows.length > 0;

  useEffect(() => {
    setConfidenceFilter(null);
    setPage(1);
  }, [mailboxScope?.mailboxHash, historyKind, brandFilter]);

  const filteredRows = useMemo(() => {
    const q = prospectSearch.trim().toLowerCase();
    let base = rows;
    if (q) {
      base = rows.filter((row) => {
        const name = (row.sender_name || "").toLowerCase();
        const company = (row.sender_company || "").toLowerCase();
        return name.includes(q) || company.includes(q);
      });
    }
    if (confidenceFilter === "human") {
      return base.filter((row) => row.is_from_human);
    }
    if (confidenceFilter === "pitch") {
      return base.filter((row) => row.is_cold_pitch);
    }
    return base;
  }, [rows, prospectSearch, confidenceFilter]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    if (confidenceFilter === "human") {
      copy.sort((left, right) => {
        const leftValue = Number(left.is_from_human_confidence ?? 0);
        const rightValue = Number(right.is_from_human_confidence ?? 0);
        const cmp = leftValue - rightValue;
        return sortDirection === "asc" ? cmp : -cmp;
      });
      return copy;
    }
    if (confidenceFilter === "pitch") {
      copy.sort((left, right) => {
        const leftValue = Number(left.is_cold_pitch_confidence ?? 0);
        const rightValue = Number(right.is_cold_pitch_confidence ?? 0);
        const cmp = leftValue - rightValue;
        return sortDirection === "asc" ? cmp : -cmp;
      });
      return copy;
    }
    copy.sort((left, right) => {
      let leftValue = left[sortField];
      let rightValue = right[sortField];
      if (sortField === "received_at") {
        leftValue = left.received_at || left.create_date;
        rightValue = right.received_at || right.create_date;
      }
      if (leftValue === rightValue) return 0;
      const basic = leftValue > rightValue ? 1 : -1;
      return sortDirection === "asc" ? basic : basic * -1;
    });
    return copy;
  }, [filteredRows, confidenceFilter, sortField, sortDirection]);

  const listConfidenceEmpty =
    rows.length > 0 && filteredRows.length === 0 && (confidenceFilter === "human" || confidenceFilter === "pitch");

  const totalPages = Math.max(Math.ceil(sortedRows.length / PAGE_SIZE), 1);

  useEffect(() => {
    setPage((previous) => Math.min(previous, totalPages));
  }, [totalPages]);

  const pagedRows = sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const headerSortArrow = (field) => {
    if (confidenceFilter === "human" && field === SORT_HUMAN) {
      return sortDirection === "asc" ? "↑" : "↓";
    }
    if (confidenceFilter === "pitch" && field === SORT_PITCH) {
      return sortDirection === "asc" ? "↑" : "↓";
    }
    if (!confidenceFilter && sortField === field) {
      return sortDirection === "asc" ? "↑" : "↓";
    }
    return "";
  };

  const toggleSort = (field) => {
    if (field === SORT_HUMAN) {
      if (confidenceFilter !== "human") {
        setConfidenceFilter("human");
        setSortDirection("desc");
      } else {
        setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
      }
      setPage(1);
      return;
    }
    if (field === SORT_PITCH) {
      if (confidenceFilter !== "pitch") {
        setConfidenceFilter("pitch");
        setSortDirection("desc");
      } else {
        setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
      }
      setPage(1);
      return;
    }
    setConfidenceFilter(null);
    if (sortField === field) {
      setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setPage(1);
  };

  return (
    <AppShell
      currentUser={props.currentUser}
      pageTitle="Mailbox history"
      pageSubtitle="Every synced email for this inbox"
    >
      <div className="space-y-6">
        <div className="hidden md:flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">
              Mailbox history
            </h1>
            <p className="font-mono text-[12px] text-theme-dim mt-1 uppercase tracking-[0.08em]">
              Every synced email for this inbox
            </p>
          </div>
          {showHistoryToolbar ? (
            <div className="flex flex-col items-stretch gap-2 md:items-end shrink-0 min-w-0 w-full md:w-auto">
              <div
                className="flex flex-wrap gap-1 rounded-lg border border-theme-border bg-theme-main/40 p-1"
                role="group"
                aria-label="History filter"
              >
                {HISTORY_KIND_OPTIONS.map((opt) => {
                  const active = historyKind === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`rounded-md px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.06em] transition-colors shrink-0 ${
                        active
                          ? "bg-theme-panel text-theme-text shadow-sm"
                          : "text-theme-dim hover:text-theme-text"
                      }`}
                      onClick={() => {
                        setHistoryKind(opt.value);
                        setPage(1);
                        if (inboundHash) navigate("/history/");
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-nowrap items-center gap-2 justify-end shrink-0 min-w-0">
                <input
                  type="search"
                  aria-label="Search sender or company"
                  placeholder="Search sender/company"
                  autoComplete="off"
                  className="min-w-[16rem] w-[22rem] max-w-full shrink-0 rounded-lg bg-theme-main border border-theme-border text-theme-text font-mono text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
                  value={prospectSearch}
                  onChange={(e) => {
                    setProspectSearch(e.target.value);
                    setPage(1);
                  }}
                />
                {showBrandSelect ? (
                  <select
                    aria-label="Filter by brand"
                    className="min-w-[10rem] max-w-[16rem] shrink-0 rounded-lg bg-theme-main border border-theme-border text-theme-text font-mono text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
                    value={brandFilter}
                    onChange={(e) => {
                      setBrandFilter(e.target.value);
                      setPage(1);
                      if (inboundHash) navigate("/history/");
                    }}
                  >
                    <option value="">All brands</option>
                    {brands.map((b) => (
                      <option key={b.hash} value={b.hash}>
                        {b.company_name?.trim() || "Brand"}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex md:hidden flex-col gap-3">
          <h1 className="font-display font-bold italic text-[28px] leading-[1.1] text-theme-on-background">
            Mailbox history
          </h1>
          <p className="font-mono text-[10px] text-theme-dim uppercase tracking-[0.08em]">
            Every synced email for this inbox
          </p>
          {showHistoryToolbar ? (
            <div className="flex flex-col gap-2 min-w-0">
              <div
                className="flex flex-wrap gap-1 rounded-lg border border-theme-border bg-theme-main/40 p-1"
                role="group"
                aria-label="History filter"
              >
                {HISTORY_KIND_OPTIONS.map((opt) => {
                  const active = historyKind === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`rounded-md px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.06em] transition-colors shrink-0 ${
                        active
                          ? "bg-theme-panel text-theme-text shadow-sm"
                          : "text-theme-dim hover:text-theme-text"
                      }`}
                      onClick={() => {
                        setHistoryKind(opt.value);
                        setPage(1);
                        if (inboundHash) navigate("/history/");
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-nowrap items-center gap-2 min-w-0">
                <input
                  type="search"
                  aria-label="Search sender or company"
                  placeholder="Search sender/company"
                  autoComplete="off"
                  className={`min-w-0 rounded-lg bg-theme-main border border-theme-border text-theme-text font-mono text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-theme-accent/60 ${
                    showBrandSelect ? "flex-1 min-w-[14rem]" : "w-full"
                  }`}
                  value={prospectSearch}
                  onChange={(e) => {
                    setProspectSearch(e.target.value);
                    setPage(1);
                  }}
                />
                {showBrandSelect ? (
                  <select
                    aria-label="Filter by brand"
                    className="min-w-0 flex-1 max-w-[min(16rem,55vw)] shrink-0 rounded-lg bg-theme-main border border-theme-border text-theme-text font-mono text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
                    value={brandFilter}
                    onChange={(e) => {
                      setBrandFilter(e.target.value);
                      setPage(1);
                      if (inboundHash) navigate("/history/");
                    }}
                  >
                    <option value="">All brands</option>
                    {brands.map((b) => (
                      <option key={b.hash} value={b.hash}>
                        {b.company_name?.trim() || "Brand"}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {!mailboxScope?.mailboxHash && !scopeBooting ? (
          <Card className="p-8 text-center">
            <p className="text-theme-text">Connect a mailbox in Settings to view history.</p>
          </Card>
        ) : null}

        {showHistoryBodySpinner ? <PageFirstLoadSpinner /> : null}

        {mailboxScope?.mailboxHash && showAuditPromo ? <InboxAuditPromoEmptyState /> : null}

        {showFilteredEmpty ? (
          <Card className="p-8 text-center">
            <p className="text-theme-text">No emails in this filter.</p>
          </Card>
        ) : null}

        {mailboxScope?.mailboxHash && !showAuditPromo && showHistoryTable && listConfidenceEmpty ? (
          <Card className="p-8 text-center">
            <p className="text-theme-text">
              {confidenceFilter === "human"
                ? "No emails flagged as from a human match this view."
                : "No emails flagged as cold pitches match this view."}
            </p>
          </Card>
        ) : null}

        {mailboxScope?.mailboxHash && !showAuditPromo && showHistoryTable && !listConfidenceEmpty ? (
          <Card className="overflow-hidden p-0">
            <table className="w-full text-left">
            <thead className="border-b border-theme-border">
              <tr>
                {[
                  ["received_at", "Date / type"],
                  ["sender_name", "Prospect"],
                  ["subject", "Message"],
                  [SORT_HUMAN, "Human"],
                  [SORT_PITCH, "Pitch"],
                  ["match_score", "Fit"],
                ].map(([field, label]) => (
                  <th key={field} className="px-5 py-3">
                    <button
                      type="button"
                      title={
                        field === SORT_HUMAN
                          ? "Show human yes only, sorted by confidence"
                          : field === SORT_PITCH
                            ? "Show cold pitch yes only, sorted by confidence"
                            : undefined
                      }
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim hover:text-theme-text focus-visible:outline-none rounded"
                      onClick={() => toggleSort(field)}
                    >
                      <span>{label}</span>
                      <span>{headerSortArrow(field)}</span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => {
                  const selected = inboundHash && row.hash === inboundHash;
                  return (
                    <tr
                      key={row.hash}
                      role="link"
                      tabIndex={0}
                      aria-current={selected ? "true" : undefined}
                      className={`cursor-pointer border-t border-theme-border transition-colors hover:bg-theme-panel focus-visible:outline focus-visible:ring-1 focus-visible:ring-theme-accent/50 ${
                        selected ? "bg-theme-panel/80" : ""
                      }`}
                      onClick={() => navigate(`/history/${row.hash}/`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/history/${row.hash}/`);
                        }
                      }}
                    >
                      <td className="px-5 py-4 font-mono text-[11px] text-theme-muted">
                        <div>{new Date(row.received_at || row.create_date).toLocaleString()}</div>
                        <div
                          className="text-[10px] text-theme-dim mt-0.5 leading-snug max-w-[min(11rem,22vw)] truncate"
                          title={row.email_type_label || ""}
                        >
                          {row.email_type_label?.trim() ? row.email_type_label : "Type not set"}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-theme-text">
                        {row.sender_name}
                        <div className="font-mono text-[11px] text-theme-dim truncate">{row.sender_company}</div>
                      </td>
                      <td className="px-5 py-4 max-w-[min(28rem,40vw)] min-w-[8rem]">
                        <div className="font-semibold text-sm text-theme-text truncate">
                          {row.subject || "(No subject)"}
                        </div>
                        <div className="font-mono text-[11px] text-theme-dim truncate">
                          {row.message_preview || "—"}
                        </div>
                      </td>
                      <td className="px-5 py-4 font-mono text-[11px] text-theme-dim">
                        <BoolConfidenceCell flag={Boolean(row.is_from_human)} confidence={row.is_from_human_confidence} />
                      </td>
                      <td className="px-5 py-4 font-mono text-[11px] text-theme-dim">
                        <BoolConfidenceCell flag={Boolean(row.is_cold_pitch)} confidence={row.is_cold_pitch_confidence} />
                      </td>
                      <td className="px-5 py-4">
                        {row.match_score != null ? (
                          <Badge variant="inherit" className={getScoreBadgeClass(row.match_score)}>
                            {row.match_score}%
                          </Badge>
                        ) : (
                          <span className="text-theme-dim font-mono text-[11px]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-theme-border px-5 py-3 font-mono text-[11px] text-theme-dim">
            <div>
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded border border-theme-border px-3 py-1 hover:border-theme-border-bright disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((previous) => previous - 1)}
              >
                Prev
              </button>
              <button
                type="button"
                className="rounded border border-theme-border px-3 py-1 hover:border-theme-border-bright disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((previous) => previous + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </Card>
        ) : null}

        <Outlet context={{ historyBrandHash: brandFilter }} />
      </div>
    </AppShell>
  );
};

export default History;
